import React, {
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useCallback,
} from 'react'
import CreatableSelect from 'react-select/creatable'
import { SingleValue, StylesConfig } from 'react-select'
import {
  fetchAllPorts,
  getCachedPortsSync,
  PortItem,
} from '../services/portService'

export interface PortSelectOption {
  value: string
  label: string
}

export interface PortSelectProps {
  value?: string | number | null
  onChange?: (e: any) => void
  onPortSelect?: (port: PortItem | null) => void
  onBlur?: (...args: any[]) => void
  onFocus?: (...args: any[]) => void
  disabled?: boolean
  name?: string
  className?: string
  placeholder?: string
  id?: string
  required?: boolean
  style?: React.CSSProperties
  isClearable?: boolean
  isLoading?: boolean
}

/**
 * Performant searchable port dropdown with browser caching.
 * Shows only the port name (no lat/long).
 * Can be used as a drop-in replacement for <input type="text" />.
 */
const PortSelect = forwardRef<any, PortSelectProps>(
  (
    {
      value,
      onChange,
      onPortSelect,
      onBlur,
      onFocus,
      disabled = false,
      name,
      className,
      placeholder = 'Select or search port...',
      id,
      required,
      style,
      isClearable = true,
    },
    ref
  ) => {
    const isSmall = (className || '').split(/\s+/).includes('form-control-sm') || (className || '').split(/\s+/).includes('form-select-sm')
    const isLarge = (className || '').split(/\s+/).includes('form-control-lg') || (className || '').split(/\s+/).includes('form-select-lg')

    // Initialize with synchronously available cached ports (0ms)
    const [ports, setPorts] = useState<PortItem[]>(() => getCachedPortsSync() || [])
    const [loading, setLoading] = useState<boolean>(ports.length === 0)
    const [inputValue, setInputValue] = useState('')

    useEffect(() => {
      let isMounted = true

      // If already loaded from synchronous cache, still ensure background sync if needed
      fetchAllPorts()
        .then((data) => {
          if (isMounted) {
            setPorts(data)
            setLoading(false)
          }
        })
        .catch((err) => {
          console.error('[PortSelect] Error loading ports:', err)
          if (isMounted) {
            setLoading(false)
          }
        })

      return () => {
        isMounted = false
      }
    }, [])

    // Map ports to options (ONLY main_port name, no lat/long)
    // We deduplicate port names while preserving distinct items
    const allOptions = useMemo<PortSelectOption[]>(() => {
      const seen = new Set<string>()
      const result: PortSelectOption[] = []

      for (const p of ports) {
        const name = (p.main_port || '').trim()
        if (name && !seen.has(name.toLowerCase())) {
          seen.add(name.toLowerCase())
          result.push({
            value: name,
            label: name, // ONLY port name, no lat/long
          })
        }
      }

      // Sort alphabetically for clean user browsing
      result.sort((a, b) => a.label.localeCompare(b.label))
      return result
    }, [ports])

    // Filter and limit options to top 100 for blazing-fast 60fps rendering
    const visibleOptions = useMemo<PortSelectOption[]>(() => {
      const search = inputValue.trim().toLowerCase()
      if (!search) {
        return allOptions.slice(0, 100)
      }

      const filtered: PortSelectOption[] = []
      // 1. Exact or startsWith matches first
      for (const opt of allOptions) {
        if (opt.label.toLowerCase().startsWith(search)) {
          filtered.push(opt)
          if (filtered.length >= 100) return filtered
        }
      }

      // 2. Contains matches second
      for (const opt of allOptions) {
        const lower = opt.label.toLowerCase()
        if (!lower.startsWith(search) && lower.includes(search)) {
          filtered.push(opt)
          if (filtered.length >= 100) return filtered
        }
      }

      return filtered
    }, [allOptions, inputValue])

    // Find currently selected option
    const selectedOption = useMemo<PortSelectOption | null>(() => {
      if (value === undefined || value === null || value === '') return null
      const strVal = String(value).trim()
      const found = allOptions.find(
        (opt) => opt.value.toLowerCase() === strVal.toLowerCase()
      )
      if (found) return found
      // If custom entered port or not in top list yet
      return { value: strVal, label: strVal }
    }, [allOptions, value])

    // Handle selection change
    const handleChange = useCallback(
      (opt: SingleValue<PortSelectOption>) => {
        const newValue = opt?.value ?? ''

        if (onPortSelect) {
          const matchedPort =
            ports.find(
              (p) =>
                p.main_port.trim().toLowerCase() === newValue.trim().toLowerCase()
            ) || null
          onPortSelect(matchedPort)
        }

        if (!onChange) return

        // Support both direct value callback and synthetic event for <input> drop-in
        const syntheticEvent = {
          target: {
            value: newValue,
            name: name || '',
            type: 'text',
          },
          currentTarget: {
            value: newValue,
            name: name || '',
          },
          preventDefault: () => {},
          stopPropagation: () => {},
        }

        try {
          // Check if onChange expects direct string or event
          onChange(syntheticEvent as any)
        } catch {
          onChange(newValue as any)
        }
      },
      [name, onChange, onPortSelect, ports]
    )

    const handleBlur = useCallback(
      (e: any) => {
        if (!onBlur) return
        const currentVal = selectedOption?.value ?? (value != null ? String(value) : '')
        onBlur({
          ...e,
          target: {
            ...(e?.target || {}),
            value: currentVal,
            name: name || '',
          },
          currentTarget: {
            ...(e?.currentTarget || {}),
            value: currentVal,
            name: name || '',
          },
          preventDefault: () => {},
          stopPropagation: () => {},
        })
      },
      [name, onBlur, selectedOption, value]
    )

    const customStyles: StylesConfig<PortSelectOption, false> = useMemo(
      () => ({
        control: (base, state) => ({
          ...base,
          borderColor: state.isFocused ? '#3b82f6' : '#e4e6ef',
          boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(59,130,246,.25)' : 'none',
          minHeight: isSmall ? 31 : isLarge ? 44 : 38,
          borderRadius: 6,
          backgroundColor: disabled ? '#eff2f5' : '#ffffff',
          color: '#181c32',
          fontSize: 14,
          fontWeight: 500,
          '&:hover': {
            borderColor: state.isFocused ? '#3b82f6' : '#b5b5c3',
          },
        }),
        menuPortal: (base) => ({
          ...base,
          zIndex: 10050,
        }),
        menu: (base) => ({
          ...base,
          borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 10050,
        }),
        menuList: (base) => ({
          ...base,
          maxHeight: 250,
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isSelected
            ? '#3b82f6'
            : state.isFocused
            ? '#eff6ff'
            : 'transparent',
          color: state.isSelected ? '#ffffff' : '#181c32',
          fontSize: 13.5,
          padding: '8px 12px',
          cursor: 'pointer',
          '&:active': {
            backgroundColor: state.isSelected ? '#2563eb' : '#dbeafe',
          },
        }),
        placeholder: (base) => ({
          ...base,
          color: '#a1a5b7',
          fontSize: 13.5,
        }),
        singleValue: (base) => ({
          ...base,
          color: '#181c32',
          fontSize: 14,
          fontWeight: 500,
        }),
        input: (base) => ({
          ...base,
          color: '#181c32',
          fontSize: 14,
        }),
        indicatorSeparator: () => ({ display: 'none' }),
        clearIndicator: (base) => ({
          ...base,
          cursor: 'pointer',
          color: '#a1a5b7',
          padding: '4px 8px',
          '&:hover': { color: '#5e6278' },
        }),
        dropdownIndicator: (base, state) => ({
          ...base,
          cursor: 'pointer',
          color: state.isFocused ? '#3b82f6' : '#a1a5b7',
          padding: '4px 8px',
          '&:hover': { color: '#3b82f6' },
        }),
      }),
      [disabled, isLarge, isSmall]
    )

    return (
      <div style={{ width: '100%', ...style }}>
        <CreatableSelect<PortSelectOption, false>
          ref={ref}
          inputId={id}
          name={name}
          isDisabled={disabled}
          isLoading={loading}
          isClearable={isClearable}
          isSearchable
          options={visibleOptions}
          value={selectedOption}
          inputValue={inputValue}
          onInputChange={(val, actionMeta) => {
            if (actionMeta.action === 'input-change') {
              setInputValue(val)
            } else if (
              actionMeta.action === 'menu-close' ||
              actionMeta.action === 'set-value'
            ) {
              setInputValue('')
            }
          }}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={onFocus}
          placeholder={loading ? 'Loading ports...' : placeholder}
          noOptionsMessage={({ inputValue: query }) =>
            loading
              ? 'Loading ports...'
              : query
              ? `No port found matching "${query}". Press Enter to use "${query}".`
              : 'Type to search ports...'
          }
          formatCreateLabel={(inputVal) => `Use "${inputVal}"`}
          styles={customStyles}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          menuPosition='fixed'
          required={required}
          classNamePrefix='port-select'
        />
      </div>
    )
  }
)

PortSelect.displayName = 'PortSelect'

export default PortSelect
